import Head from 'next/head';
import React from 'react';
import Header from '../Chrome/Header';
import dynamic from 'next/dynamic';
import classNames from 'classnames';
import { CSSProp } from 'styled-components';
import PageMessage from '@sls-react/components/Layout/PageMessage';
import generatePageTitle from '@sls-react/utils/generatePageTitle';
import DefaultCanonical from '@sls-react/components/Layout/DefaultCanonical';
import useChromelessContext from '@sls-react/contexts/ChromelessContext';
import ErrorPageImage from '../ErrorPageImage';
import ErrorBoundary from '../ErrorBoundary';
import MicroBanner from '@sls-react/components/Chrome/Banners/MicroBanner';
import SimpleFooter from '@sls-react/components/Layout/SimpleFooter';

const description =
  'SidelineSwap is where athletes buy and sell their gear. Find great deals on new and used equipment, or list your gear for free in seconds.';

export interface PageMetaProps {
  canonical?: string;
  description?: string;
}

const Footer = dynamic(() => import('../Chrome/Footer'));

export interface LayoutProps {
  children: React.ReactNode;
  className?: string;
  container?: boolean;
  css?: CSSProp;
  excludeAppCta?: boolean;
  excludeBanners?: boolean;
  excludeMessage?: boolean;
  excludeMobileSearch?: boolean;
  meta?: PageMetaProps;
  simpleFooter?: boolean;
  title?: string;

  /**
   * Defaults to " | Buy and Sell on SidelineSwap".
   * Can be overwritten by passing an empty string
   */
  titleTemplate?: string;
}

const Layout: React.FC<LayoutProps> = ({
  children,
  className,
  meta,
  container = true,
  excludeAppCta,
  excludeBanners,
  excludeMessage,
  excludeMobileSearch,
  simpleFooter,
  title,
  titleTemplate,
}) => {
  const chromeless = useChromelessContext();

  let footer: React.ReactNode = null;

  if (!chromeless.isChromeless) {
    if (simpleFooter) {
      footer = <SimpleFooter />;
    } else {
      footer = <Footer excludeAppCta={excludeAppCta} />;
    }
  }

  return (
    <>
      <Head>
        <title>{generatePageTitle(title, titleTemplate)}</title>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, shrink-to-fit=no, maximum-scale=5, user-scalable=yes"
        />

        <meta name="description" content={meta?.description || description} />
        <meta
          name="og:description"
          content={meta?.description || description}
        />
      </Head>
      <DefaultCanonical canonical={meta?.canonical} />

      {!chromeless.isChromeless && (
        <Header
          excludeBanners={excludeBanners}
          excludeMobileSearch={excludeMobileSearch}
        />
      )}
      <ErrorBoundary
        fallback={
          <>
            <h1 className="my-8 text-center">Something went wrong...</h1>
            <ErrorPageImage />
          </>
        }
      >
        <MicroBanner />
        <main className={classNames(className, { container })}>
          {!excludeMessage && <PageMessage />}
          {children}
        </main>
      </ErrorBoundary>

      {footer}
    </>
  );
};

export default Layout;
